import React, { useCallback, useEffect, useState } from 'react';
import { Box, FormControl, Grid, MenuItem, TextField, Typography, InputAdornment } from '@mui/material';
import { Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, AreaChart, Area, Label } from "recharts";
import { AddIcCall, FilterList, Call, AccessTime, WarningAmber, } from '@mui/icons-material';
import api from "../api";
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { ChartContainer } from '../components/Chart';
import { formatDuration } from '../components/Format';
import { formatDate } from '../components/Format';
import Wrapper from '../components/Wrapper';
import ShimmerLoader from '../components/ShimmerLoader';
import NoDataFound from '../components/NoDataFound';
import { downloadAvgCallMinutesByAssistantCSV, downloadCallAnalysisReport, downloadFirstCallResolutionPerAssistantCSV } from '../components/download';
import { CustomRadarChart } from '../components/CustomRadarChart';
import MetricCard from '../components/Assistant/MetricCard';
import { textFieldSx } from '../styles/GlobalStyles';
import AssistantPerformance from '../components/AssistantOverviewCharts/AssistantPerformance';
import { ASSISTANT_ROW_HEIGHT, RECORD_LIMIT, RECORDS_PER_PAGE } from '../components/Constants';
import { updateDashboardState } from './utils/fcrHelper';
import { toast } from 'sonner';
const Overview = () => {
  // Get the organizationId from URL parameters
  const { company_id } = useParams();

  const [organization, setOrganization] = useState(company_id || '');
  const [organizationData, setorganizationData] = useState('');
  const [assistant, setAssistant] = useState('');
  const [filter, setFilter] = useState('monthly');
  const [assistantList, setAssistantList] = useState([]);
  const [filteredAssistantList, setFilteredAssistantList]=useState([])
  const [organizationList, setOrganizationList] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    totalCallDuration: { loading: true, data: {} },
    totalCalls: { loading: true, data: {} },
    totalCost: { loading: true, data: {} },
    averageCost: { loading: true, data: {} },
    transferRate: { loading: true, data: {} },
    abandonmentRate: { loading: true, data: {} },
    callActions: { loading: true, data: {} },
    averageCallPerAssistant: { loading: true, data: [] },
    assistantActions: { loading: true, data: {} },
    firstCallResolution: { loading: true, data: [] },
    actionsByAssistant: { loading: true, data: [] },
  });
  const [dataFound, setDataFound] = useState(true);
  const [assistantPerformance, setAssistantPerformance] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const token = JSON.parse(localStorage.getItem("token"))?.token;
  
  const getOrganization = useCallback(async () => {
    if (!company_id) return;

    try {
        const [orgRes, assistantsRes] = await Promise.all([
            api.get(`/organizations/${company_id}`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                }
            }),
            api.post("/organizations/assistants-by-company", {
                organizationId: company_id
            }, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                }
            })
        ]);

        setorganizationData(orgRes.data);
        setFilteredAssistantList(assistantsRes.data?.data || []);
    } catch (error) {
        console.error("Failed to fetch organization data:", error?.response?.data || error.message);
        alert("Error fetching organization data. Please try again later.");
    }
}, [token, company_id]);


  useEffect(() => {
    // Update organization state if URL parameter changes
    if (company_id) {
      setOrganization(company_id);
      getOrganization();
    }
  }, [company_id, getOrganization]);

  const fetchData = useCallback(async (url, key) => {
    try {
      const response = await api.get(`${url}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      setDashboardData(prev => ({
        ...prev,
        [key]: { loading: false, data: response.data }
      }));
      return response.data;
    } catch (error) {
      console.error('Error fetching data:', error);
      setDashboardData(prev => ({
        ...prev,
        [key]: { loading: false, data: {} }
      }));
      return null;
    }
  }, [token]);

  const handleOrg = async(e) => {
    const selectedOrg=e.target.value
    setOrganization(selectedOrg)
    if(selectedOrg !== 'All'){
      const getFilteredAssistants=await api.post("/superadmin/assistants-by-company",{
        organizationId: selectedOrg
      },{
        headers: {
          "Authorization": `Bearer ${token}`
        }
      })
      setFilteredAssistantList(getFilteredAssistants.data.data)
    }
    else{
      setFilteredAssistantList(assistantList)
    }
    
  }
  const getAllAssistants = useCallback(async () => {
    const data = await fetchData('/assistants?limit=1000', 'assistantList');
    if (data) { setAssistantList(data.data.assistants) }
    else {
      setDataFound(false)
    }
  }, [fetchData]);

  const getAllOrganizations = useCallback(async () => {
    const data = await fetchData('/organizations?limit=1000', 'organizationList');
    if (data) setOrganizationList(data.data.organizations);
  }, [fetchData]);

  const getOverviewData = useCallback(async () => {
    if (!token) return;
  
    const buildUrl = (baseUrl) => {
      const params = new URLSearchParams();
      if (filter) params.append('filter', filter);
      if (organization && organization !== "All") params.append('organizationId', organization);
      if (assistant && assistant !== "All") params.append('assistantId', assistant);
      return `${baseUrl}?${params.toString()}`;
    };
  
    const endpoints = [
      { key: "totalCallDuration", url: buildUrl('/call-logs/total-time') },
      { key: "averageCost", url: buildUrl('/call-logs/average-cost') },
      { key: "totalCost", url: buildUrl('/call-logs/total-cost') },
      { key: "totalCalls", url: buildUrl('/call-logs/total-calls') },
      { key: "abandonmentRate", url: buildUrl('/call-logs/call-abandonment-rate') },
      { key: "transferRate", url: buildUrl('/call-logs/transfer-rate') },
      { key: "callActions", url: buildUrl('/call-logs/call-actions') },
      { key: "averageCallPerAssistant", url: buildUrl('/call-logs/average-call-per-assistant') },
      { key: "assistantActions", url: buildUrl('/call-logs/actions-by-assistant') },
      { key: "firstCallResolution", url: buildUrl('/call-logs/first-call-resolution') },
      { key: "actionsByAssistant", url: buildUrl('/call-logs/actions-by-assistant') }
    ];
  
    // Set loading state for all endpoints
    setDashboardData(prev => {
      const newState = { ...prev };
      endpoints.forEach(({ key }) => {
        newState[key] = { loading: true, data: {} };
      });
      return newState;
    });
  
    try {
      const results = await Promise.all(
        endpoints.map(async ({ url, key }) => {
          try {
            const response = await api.get(url, {
              headers: { "Authorization": `Bearer ${token}` }
            });
            return { key, data: response.data, error: null };
          } catch (error) {
            console.error(`Error fetching ${key}:`, error);
            return { key, data: {}, error };
          }
        })
      );
  
      // Update dashboard data with all results
      setDashboardData(prev =>
        updateDashboardState(prev, results, assistantList)
      );

  
    } catch (error) {
      console.error('Error fetching overview data:', error);
      setDashboardData(prev => {
        const newState = { ...prev };
        endpoints.forEach(({ key }) => {
          newState[key] = { loading: false, data: {}, error: 'Failed to fetch data' };
        });
        return newState;
      });
    }
  }, [token, filter, organization, assistant, assistantList]); // Added assistantList as dependency

  const getAssistantPerformance = useCallback(async () => {
    try {
      const params = {
        limit: RECORD_LIMIT,
        filter,
      };

      if (organization && organization !== "All") {
        params.organizationId = organization;
      }

      if (assistant && assistant !== "All") {
        params.assistantId = assistant;
      }

      const response = await api.get("/call-logs/assistant-performance", {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      const allData = response.data?.data || [];

      const dataMap = new Map();
      allData.forEach(item => {
        dataMap.set(item.assistantName, item);
      });

      const validAssistantNames = assistantList.map(a => a.Name);
      const mergedData = validAssistantNames.map(name =>
        dataMap.get(name) || {
          assistantName: name,
          totalCalls: 0,
          successfulCalls: 0,
          failedCalls: 0,
        }
      );

      setTotalCount(mergedData.length);
      setTotalPages(Math.ceil(mergedData.length / RECORDS_PER_PAGE));
      setAssistantPerformance(mergedData);

    } catch (error) {
      console.error("Failed to fetch assistant performance:", error?.response?.data || error.message);
      toast.error("Failed to load assistant performance data. Please try again.");
    }
  }, [token, filter, organization, assistant, assistantList]);



  useEffect(() => {
    if (token) {
      getAllAssistants(); // First fetch the assistants
    }
  }, [getAllAssistants, token]);

  // Separate useEffect for data that depends on assistantList
  useEffect(() => {
    if (token && assistantList.length > 0) {
      getOverviewData();
      getAllOrganizations();
      getAssistantPerformance();
    }
  }, [getOverviewData, getAllOrganizations, getAssistantPerformance, token, assistantList]);

  const isIncrease = useCallback((percentageChange) => {
    return percentageChange && parseFloat(percentageChange) >= 0;
  }, []);

  const metricCards = [
    {
      title: "Total Call Duration",
      icon: <AccessTime sx={{ color: "#FFF" }} />,
      value: formatDuration(dashboardData.totalCallDuration.data?.totalDuration),
      inc: isIncrease(dashboardData.totalCallDuration.data?.percentageChange),
      change: dashboardData.totalCallDuration.data?.percentageChange,
      subtitle: "Total call duration accumulated over the selected time period.",
      chartData: dashboardData.totalCallDuration.data?.dataPoints,
      loading: dashboardData.totalCallDuration.loading,
      yAxisLabel: "Total Duration (Hours)",
      yAxisFormatter: (value) => formatDuration(value),
      chartKey: "totalDuration"
    },
    {
      title: "Total Calls",
      icon: <Call sx={{ color: "#FFF" }} />,
      value: dashboardData.totalCalls.data?.totalCalls,
      inc: isIncrease(dashboardData.totalCalls.data?.percentageChange),
      change: dashboardData.totalCalls.data?.percentageChange,
      subtitle: "Total number of calls across the selected assistants over the selected time period.",
      chartData: dashboardData.totalCalls.data?.dataPoints,
      loading: dashboardData.totalCalls.loading,
      yAxisLabel: "Total Calls",
      yAxisFormatter: (value) => value,
      chartKey: "totalCalls"
    },
    {
      title: "Call Abandonment Rate",
      icon: <WarningAmber sx={{ color: "#FFF" }} />,
      value: dashboardData.abandonmentRate.data?.currentPeriod?.abandonmentRate.toFixed(2),
      inc: isIncrease(dashboardData.abandonmentRate.data?.percentageChange),
      change: dashboardData.abandonmentRate.data?.percentageChange,
      subtitle: "Percentage of calls that were abandoned before connecting to an agent over the selected time period.",
      chartData: dashboardData.abandonmentRate.data?.dataPoints,
      loading: dashboardData.abandonmentRate.loading,
      yAxisLabel: "Abandonment Rate (%)",
      yAxisFormatter: (value) => `${value}%`,
      chartKey: "abandonmentRate"
    },
    {
      title: "Transfer Rate",
      icon: <AddIcCall sx={{ color: "#FFF" }} />,
      value: dashboardData.transferRate.data?.currentPeriod?.transferRate,
      inc: isIncrease(dashboardData.transferRate.data?.percentageChange),
      change: dashboardData.transferRate.data?.percentageChange,
      subtitle: "Percentage of calls that were transferred to a human agent over the selected time period.",
      chartData: dashboardData.transferRate.data?.dataPoints,
      loading: dashboardData.transferRate.loading,
      yAxisLabel: "Transfer Rate (%)",
      yAxisFormatter: (value) => `${value}%`,
      chartKey: "transferRate"
    }
    
  ];



  if (!dataFound) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: "100%", height: "100%" }}>
        <NoDataFound
          title='No Metrics Yet – Get Started'
          subtitle="No metrics available yet. Set up an assistant to begin tracking performance insights."
          buttonLabel='Set Up Assistant'
          buttonLink='/assistants/create'
        />
      </Box>
    )
  }


  return (
    <>
      <Navbar title={`Overview | ${organizationData?.Name || 'Dashboard'}`} showReleaseButton={true} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: "2rem" }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: "wrap", gap: "1rem" }}>
          <Box>
            <Typography variant="h6" component="div">Dashboard Summary</Typography>
            <Typography variant="body2" color='text.secondary'>
              A holistic view of the admin portal.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            {!company_id && (
              <FormControl size='small' sx={{ width: "12rem", bgcolor: "#FFF", height: 'fit-content' }}>
                <TextField
                  select
                  labelId="company"
                  id="company"
                  value={organization}
                  label="Company"
                  size="small"
                  displayEmpty
                  onChange={handleOrg}
                  sx={textFieldSx}
                >
                  <MenuItem value="All">All</MenuItem>
                  {organizationList.map((organization) => (
                    <MenuItem value={organization._id} key={organization._id}>{organization.Name}</MenuItem>
                  ))}
                </TextField>
              </FormControl>
            )}

            <FormControl size='small' sx={{ width: "12rem", bgcolor: "#FFF", height: 'fit-content' }}>
              <TextField
                select
                labelId="Assistant"
                id="assistant"
                value={assistant}
                label="Assistant"
                size="small"
                onChange={(e) => setAssistant(e.target.value)}
                sx={textFieldSx}
              >
                <MenuItem value="All">All</MenuItem>
                {filteredAssistantList.map((assistant) => (
                  <MenuItem value={assistant._id} key={assistant._id}>{assistant.Name.slice(0, 20)}</MenuItem>
                ))}
              </TextField>
            </FormControl>

            <FormControl size='small' sx={{ width: "10rem", bgcolor: "#FFF", height: 'fit-content' }}>
              <TextField
                select
                labelId="filter"
                id="filter"
                value={filter}
                label="Filter"
                sx={textFieldSx}
                onChange={(e) => setFilter(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><FilterList /></InputAdornment>,
                }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </TextField>
            </FormControl>
          </Box>
        </Box>
        <Grid container spacing={2}>
  {metricCards.map((card, index) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
      <MetricCard
        title={card.title}
        icons={<Wrapper>{card.icon}</Wrapper>}
        value={card.value}
        inc={card.inc}
        change={card.change}
        subtitle={card.subtitle}
        chart={
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={card.chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                interval="preserveStartEnd"
                minTickGap={50}
                textAnchor="middle"
                fontSize={10}
                height={60}
              >
                <Label value="Date" position="insideBottom" offset={-5} />
              </XAxis>
              <YAxis
                fontSize={10}
                tickFormatter={card.yAxisFormatter}
                width={80}
              >
                <Label value={card.yAxisLabel} angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
              </YAxis>
              <Tooltip
                labelFormatter={(label) => `Date Range: ${formatDate(label)}`}
                formatter={(value) => [value, card.title]}
              />
              <Area
                type="monotone"
                dataKey={card.chartKey}
                stroke="#02042D"
                fill="#A5A8E6"
                fillOpacity={0.3}
                activeDot={{ r: 8 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        }
        loading={card.loading}
      />
    </Grid>
  ))}
</Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ChartContainer
              title="Call Analysis"
              subtitle="Insights on call performance on various factors."
              download={() => downloadCallAnalysisReport({ filter, organization: organization === "All" ? null : organization })}
              chart=
              {
                dashboardData.callActions.loading ? (
                  <ShimmerLoader height="300px" />
                ) : (
                  dashboardData.callActions.data?.data &&
                  Object.keys(dashboardData.callActions.data.data).length > 0 ? (
                    <CustomRadarChart
                      data={[
                        {
                          name: "Call Transferred",
                          value: dashboardData.callActions.data.data.transferedResult || 0,
                        },
                        {
                          name: "Goal Achievement",
                          value: dashboardData.callActions.data.data.goalAchievementResult || 0,
                        },
                        {
                          name: "User Satisfaction",
                          value: dashboardData.callActions.data.data.userSatisfactionResult || 0,
                        },
                        {
                          name: "SOP Adherence (%)",
                          value: dashboardData.callActions.data.data.scriptAdherenceResult || 0,
                        },
                        {
                          name: "Call Abandonment",
                          value: (dashboardData.callActions.data.data.callAbandonmentResult || 0).toFixed(2),
                        },
                      ]}
                    />
                  ) : (
                    "No data found for applied filters."
                  )
                )
              }
            />
          </Grid >

          <Grid item xs={12} md={6}>
            <ChartContainer
              title="Average Handle Time per Assistant"
              subtitle="Average time spent by each assistant on calls."
              download={() => downloadAvgCallMinutesByAssistantCSV({ filter, organization, assistant })}
              chart={
                dashboardData.averageCallPerAssistant.loading ? (
                  <ShimmerLoader height="300px" />
                ) : (
                  dashboardData.averageCallPerAssistant.data?.assistants?.length ? (
                      <ResponsiveContainer
                        width="100%"
                        height={Math.max(400, dashboardData.averageCallPerAssistant.data.assistants.length * ASSISTANT_ROW_HEIGHT)}
                      >
                        <BarChart
                          data={dashboardData.averageCallPerAssistant.data.assistants}
                          layout="vertical"
                          margin={{ top: 10, right: 30, left: 0, bottom: 30 }}
                        >
                          <CartesianGrid stroke="#EEE" strokeDasharray="3 3" />
                          <XAxis
                            fontSize={10}
                            type="number"
                            tickFormatter={(value) => `${value % 60} S`}
                          >
                            <Label
                              value="Time"
                              position="bottom"
                              offset={-5}
                              style={{ textAnchor: "middle", fontSize: "14px", fontWeight: "500" }}
                            />
                          </XAxis>
                          <YAxis
                            dataKey="assistantName"
                            type="category"
                            width={100}
                            fontSize={10}
                          />
                          <Tooltip
                            formatter={(value, name) => [`${value} S`, "Average Duration"]}
                            contentStyle={{
                              backgroundColor: "#FFF",
                              borderColor: "none",
                              color: "#000",
                              border: "1px solid #EEE",
                              padding: "0.5rem",
                            }}
                            itemStyle={{
                              color: "#000",
                            }}
                            cursor={{ fill: "transparent" }}
                          />
                          <Bar dataKey="avgDuration" fill="#02042D" />
                        </BarChart>
                      </ResponsiveContainer>
                  ) : "No data found for applied filters."
                )
              }
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ChartContainer
              title="First Call Resolution (FCR) per Assistant"
              subtitle="Percentage of calls resolved on the first attempt by each assistant."
              download={() => downloadFirstCallResolutionPerAssistantCSV({ filter, organization, assistant })}
              chart={
                dashboardData.firstCallResolution.loading ? (
                  <ShimmerLoader height="300px" />
                ) : (
                  dashboardData.firstCallResolution.data.length ? (
                    <BarChart
                      data={dashboardData.firstCallResolution.data}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" fontSize={10}>
                        <Label
                          value="Count"
                          position="bottom"
                          style={{ textAnchor: "middle" }}
                        />
                      </XAxis>
                      <YAxis
                        dataKey="assistantName"
                        type="category"
                        fontSize={10}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#FFF",
                          borderColor: "none",
                          color: "#000",
                          border: "1px solid #EEE",
                          padding: "0.5rem",
                        }}
                        itemStyle={{
                          color: "#000",
                        }}
                        cursor={{ fill: "transparent" }}
                      />
                      <Bar dataKey="firstCallResolutionCount" fill="#02042D" />
                    </BarChart>
                  ) : "No data found for applied filters."
                )
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <AssistantPerformance
              assistantPerformance={assistantPerformance}
              totalCount={totalCount}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              filter={filter}
              assistant={assistant}
              assistantList={assistantList} // Pass the list here
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Overview;